import { type CapabilityConfig, type Message, MessageClass } from './types';

const VALID_MESSAGE_CONFIG_KEYS = ['class', 'type', 'payload'] as const;

export function hasOnlyKeysFromSet(
  obj: Record<string, unknown>,
  validKeys: readonly string[],
): boolean {
  const keys = Object.keys(obj);
  return keys.every((k) => validKeys.includes(k));
}

export function hasOnlyValuesFromSet(
  values: unknown[],
  validKeys: readonly unknown[],
): boolean {
  return values.every((k) => validKeys.includes(k));
}

/**
 * Determine if key exists in config, taking to account null (it is returned for all fields without value from api)
 */
export function hasValue<K extends string>(
  obj: Record<string, unknown>,
  key: K,
): obj is { [key in K]: unknown } {
  return key in obj && obj[key] !== null;
}

/**
 * Check if messages config is valid
 */
export function isValidMessageConfig(config: unknown): config is Message {
  if (typeof config !== 'object' || config === null) {
    return false;
  }

  if (
    !hasOnlyKeysFromSet(
      config as Record<string, unknown>,
      VALID_MESSAGE_CONFIG_KEYS,
    )
  ) {
    return false;
  }

  if (!('type' in config) || typeof config.type !== 'string') {
    return false;
  }

  if (
    !('class' in config) ||
    (config.class !== MessageClass.Action &&
      config.class !== MessageClass.Event)
  ) {
    return false;
  }

  return true;
}

/**
 * Check if context config is valid
 */
export function isValidContextConfig(
  config: CapabilityConfig,
  validContextKeys: readonly string[],
): boolean {
  if ('context' in config) {
    if (!Array.isArray(config['context'])) {
      return false;
    }

    // Config must only contain valid context values
    if (!hasOnlyValuesFromSet(config['context'], validContextKeys)) {
      return false;
    }
  }

  return true;
}

export function isStringArray(val: unknown): val is string[] {
  return Array.isArray(val) && val.every((item) => typeof item === 'string');
}

/**
 * Check if context is valid: keys must be in the allowed set, and value types
 * are enforced. Keys in arrayContextKeys must have undefined or string[] values;
 * all other keys must have undefined or string values.
 */
export function isValidContext<Context>(
  context: unknown,
  validContextKeys: readonly string[],
  requiredContextKeys: readonly string[] = [],
  arrayContextKeys: readonly string[] = [],
): context is Context {
  if (typeof context !== 'object' || context === null) {
    return false;
  }

  const ctx = context as Record<string, unknown>;

  for (const key of Object.keys(ctx)) {
    if (!validContextKeys.includes(key)) {
      return false;
    }
    const val = ctx[key];
    if (val === undefined) continue;
    if (arrayContextKeys.includes(key)) {
      if (!isStringArray(val)) return false;
    } else {
      if (typeof val !== 'string') return false;
    }
  }

  return requiredContextKeys.every((key) => ctx[key] !== undefined);
}
